import React, { useState, useEffect } from 'react';
import { postAPI } from '../../../../app/api';
import Swal from 'sweetalert2';
const { REACT_APP_BRANDURL } = process.env;

export default function GallerySection() {
    const [galleries, setGalleries] = useState([]);
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedGallery, setSelectedGallery] = useState(null);

    // Fetch all galleries
    useEffect(() => {
        const initFetch = async () => {
            try {
                setLoading(true);
                const response = await postAPI('gallery/list', {});
                if (response?.status === 1) {
                    const gals = response?.data || [];
                    setGalleries(gals);
                    if (gals.length > 0) {
                        setSelectedGallery(gals[0].id);
                        const fileResponse = await postAPI('gallery/listFiles', { folder_id: gals[0].id });
                        if (fileResponse?.status === 1) {
                            setGalleryFiles(fileResponse?.data || []);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching galleries:', error);
                Swal.fire('Error', 'Failed to fetch galleries', 'error');
            } finally {
                setLoading(false);
            }
        };
        initFetch();
    }, []);

    const fetchGalleryFiles = async (galleryId) => {
        try {
            const response = await postAPI('gallery/listFiles', { folder_id: galleryId });
            if (response?.status === 1) {
                setGalleryFiles(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching gallery files:', error);
        }
    };

    const handleGallerySelect = (galleryId) => {
        setSelectedGallery(galleryId);
        fetchGalleryFiles(galleryId);
    };

    const handleToggleHomepage = async (fileId, isSelected) => {
        try {
            const response = await postAPI('gallery/toggle-homepage', { 
                id: fileId, 
                show_in_homepage: isSelected ? 0 : 1 
            });
            if (response?.status === 1) {
                Swal.fire('Success', `Image ${isSelected ? 'removed from' : 'added to'} homepage`, 'success');
                // Update local state instead of re-fetching
                setGalleryFiles(prevFiles => 
                    prevFiles.map(file => 
                        file.id === fileId 
                            ? { ...file, show_in_homepage: isSelected ? 0 : 1 }
                            : file
                    )
                );
            } else {
                Swal.fire('Error', response?.msg || 'Failed to update image', 'error');
            }
        } catch (error) {
            console.error('Toggle homepage error:', error);
            Swal.fire('Error', 'Failed to update image', 'error');
        }
    };

    return (
        <>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <h3 className="mb-0">Gallery - Select Images for Homepage</h3>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-3">
                        <div className="card gallery-list">
                            <div className="card-body">
                                <h6 className="card-title mb-3">Gallery Folders</h6>
                                {loading ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="list-group ">
                                        {galleries && galleries.length > 0 ? (
                                            galleries.map((gallery, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className={`list-group-item list-group-item-action ${selectedGallery === gallery.id ? 'active' : ''}`}
                                                    onClick={() => handleGallerySelect(gallery.id)}
                                                >
                                                    <div className="text-start">
                                                        <h6 className="mb-0">{gallery.folder_name}</h6>
                                                        <small className={`badge badge-${gallery.status === 1 ? 'success' : 'danger'}`}>
                                                            {gallery.status === 1 ? 'Active' : 'Inactive'}
                                                        </small>
                                                    </div>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="alert alert-info mb-0">No galleries found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-9">
                        <div className="card">
                            <div className="card-body">
                                {selectedGallery ? (
                                    <>
                                        <h6 className="card-title mb-3">
                                            Images - {galleries.find(g => g.id === selectedGallery)?.folder_name || 'Gallery'}
                                        </h6>
                                        <div className="alert alert-info">
                                            <strong>💡 Tip:</strong> Check the checkbox next to an image to display it on the homepage. Uncheck to hide it.
                                        </div>
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : galleryFiles && galleryFiles.length > 0 ? (
                                            <div className="row g-3">
                                                {galleryFiles.map((file, index) => (
                                                    <div key={index} className="col-lg-4 col-md-6">
                                                        <div className="card gallery-item">
                                                            <div className="position-relative">
                                                                <img 
                                                                    src={`${file.file_url}`}
                                                                    className="card-img-top"
                                                                    alt={file.gallery_file_name}
                                                                    style={{ height: '200px', objectFit: 'cover' }}
                                                                />
                                                                <div className="position-absolute bottom-0 end-0 m-2">
                                                                    <div className="form-check">
                                                                        <input 
                                                                            className="form-check-input" 
                                                                            type="checkbox" 
                                                                            id={`chk-${file.id}`}
                                                                            checked={file.show_in_homepage === 1}
                                                                            onChange={() => handleToggleHomepage(file.id, file.show_in_homepage === 1)}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`chk-${file.id}`}>
                                                                            Show
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="card-body">
                                                                <p className="card-text mb-2">
                                                                    <strong>{file.gallery_file_name}</strong>
                                                                </p>
                                                                <div className="d-flex gap-2 flex-wrap">
                                                                    <span className={`badge badge-sm badge-${file.status === 1 ? 'success' : 'danger'}`}>
                                                                        {file.status === 1 ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                    {file.show_in_homepage === 1 && (
                                                                        <span className="badge badge-sm badge-info">
                                                                            On Homepage
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="alert alert-warning">
                                                No images in this gallery
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="alert alert-info">
                                        Select a gallery folder from the left panel to view and manage images
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
